/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { PRODUCT_DOMAIN } from "@/constants";
import { ApiErrorProps } from "@/types/ApiErrorType";
import { QuoteData } from "@/types/quote/quote-data";
import { modifyQuestionQuotePayload } from "@/utils/common.utils";

import {
  createQuoteOnQuestion,
  updateQuoteOnQuestion,
  getQuoteData,
  saveQuestionDataBFF,
} from "./quote.action";

const initialState = {
  type: "Quote",
  referenceId: "",
  version: "",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    dynamicDocuments: null,
    lastVisitedPage: "",
    organiser: null,
    tripDetails: null,
    travellerDetails: null,
    marketingConsent: null,
    priceResult: null,
    optionalExtras: null,
    dynamicCoverLimit: null,
    paymentDetails: null,
    mtaMedicalConsent: {
      isTravellingBeforeRenewal: null,
      validForMtaCreation: false,
      currentPolicyRemainingDays: 0,
      mtaTravelers: [],
    },
  },
  isCoverLevelChanged: false,
  isTripTypeChanged: false,
  isLoadingCoverState: false,
  quoteCompleted: {
    completed: false,
  },
  loading: false,
  error: null,
  quoteErrorCode: null,
  turnstileToken: null,
  turnstileErrorCode: null,
  isUnsupportedBrowser: false,
  onLoadingForExpiredToken: false,
} as unknown as QuoteData;

const INVALID_POSTAGE = "-1";
const DEFAULT_POSTAGE = "0";
/**
 * Normalizes postageOption value: converts "-1" to "0"
 * Backend sometimes returns -1, which should be treated as "0" (Email only option)
 * Handles null, undefined, and empty payloads safely
 */
const normalizePostageOption = <T>(payload: T): T => {
  // Handle null or undefined payload
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const typedPayload = payload as Record<string, unknown>;
  const attributes = typedPayload.attributes as
    | Record<string, unknown>
    | null
    | undefined;

  const paymentDetails = attributes?.paymentDetails as
    | Record<string, unknown>
    | null
    | undefined;

  const postageOption = paymentDetails?.postageOption;

  if (postageOption === INVALID_POSTAGE || postageOption === -1) {
    return {
      ...payload,
      attributes: {
        ...attributes,
        paymentDetails: {
          ...paymentDetails,
          postageOption: DEFAULT_POSTAGE,
        },
      },
    } as T;
  }
  return payload;
};

const quoteSlice = createSlice({
  name: "quote",
  initialState,
  reducers: {
    setTurnstileToken: (state, action: PayloadAction<string | null>) => {
      state.turnstileToken = action.payload;
      state.turnstileErrorCode = null;
    },
    setTurnstileErrorCode: (state, action: PayloadAction<string | null>) => {
      state.turnstileErrorCode = action.payload;
    },
    setUnsupportedBrowserFlag: (state, action: PayloadAction<boolean>) => {
      state.isUnsupportedBrowser = action.payload;
    },
    setOnLoadingForExpiredToken: (state, action: PayloadAction<boolean>) => {
      state.onLoadingForExpiredToken = action.payload;
    },
    clearTurnstileToken: (state) => {
      state.turnstileToken = null;
    },
    clearTurnstileErrorCode: (state) => {
      state.turnstileErrorCode = null;
    },
    clearUnsupportedBrowserFlag: (state) => {
      state.isUnsupportedBrowser = undefined;
    },
    clearOnLoadingForExpiredToken: (state) => {
      state.onLoadingForExpiredToken = undefined;
    },
    clearQuote: () => {
      return initialState;
    },
    setQuote: (state, action: PayloadAction<typeof initialState>) => {
      const normalizedPayload = normalizePostageOption(action.payload);
      return {
        channel: state.channel,
        ...normalizedPayload,
      };
    },
    setQuoteErrorCode: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        quoteErrorCode: action.payload,
      };
    },
    clearQuoteErrorCode: (state) => {
      return {
        ...state,
        quoteErrorCode: null,
      };
    },
    updateQuote: (state, action: PayloadAction<typeof initialState>) => {
      const normalizedPayload = normalizePostageOption(action.payload);
      return {
        ...state,
        ...normalizedPayload,
        quoteErrorCode: null,
      };
    },
    updateQuoteCompleted: (
      state,
      action: PayloadAction<(typeof initialState)["quoteCompleted"]>
    ) => {
      state.quoteCompleted = action.payload;
      return state;
    },
    setCoverLevelChangedState: (state, action: PayloadAction<boolean>) => {
      // update isCoverLevelChanged
      state.isCoverLevelChanged = action.payload;
      return state;
    },
    setTripTypeChangedState: (state, action: PayloadAction<boolean>) => {
      state.isTripTypeChanged = action.payload;
      return state;
    },
    updateisLoadingCoverState: (state, action: PayloadAction<boolean>) => {
      state.isLoadingCoverState = action.payload;
      return state;
    },
    setQuestionData: (state, action: PayloadAction<any>) => {
      const normalizedPayload = normalizePostageOption(action?.payload);
      const quotoEditResponse = modifyQuestionQuotePayload(
        normalizedPayload,
        state
      );

      return {
        ...state,
        ...(quotoEditResponse as QuoteData),
        loading: false,
      };
    },
    clearQuestion: () => {
      return initialState;
    },
    updateOrganiserPMI: (state, action: PayloadAction<{ hasPMI: boolean }>) => {
      if (state.attributes?.organiser) {
        state.attributes.organiser.hasPMI = action.payload.hasPMI;
      }
      return state;
    },
    updateRequiredInformation: (
      state,
      action: PayloadAction<{
        organiser: {
          firstLineOfAddress: string;
          secondLineOfAddress?: string;
          city: string;
          postCode: string;
          identificationType: string;
          identificationNumber: string;
        };
        travellers: Array<{
          encryptedId: string;
          identificationType?: string;
          identificationNumber?: string;
        }>;
      }>
    ) => {
      // Update organiser address and identification
      if (state.attributes?.organiser) {
        state.attributes.organiser.firstLineOfAddress =
          action.payload.organiser.firstLineOfAddress;
        state.attributes.organiser.secondLineOfAddress =
          action.payload.organiser.secondLineOfAddress;
        state.attributes.organiser.city = action.payload.organiser.city;
        state.attributes.organiser.postCode = action.payload.organiser.postCode;
        state.attributes.organiser.identificationType =
          action.payload.organiser.identificationType;
        state.attributes.organiser.identificationNumber =
          action.payload.organiser.identificationNumber;
      }
      // Update traveller identifications
      if (
        state.attributes?.travellerDetails?.traveller &&
        Array.isArray(state.attributes.travellerDetails.traveller)
      ) {
        action.payload.travellers.forEach((traveller) => {
          const existing = state.attributes?.travellerDetails?.traveller?.find(
            (t: any) => t.encryptedId === traveller.encryptedId
          );
          if (existing) {
            // Only update identification fields if BOTH are present in payload
            // This preserves existing values when fields are not sent
            if (
              traveller.identificationType !== undefined &&
              traveller.identificationNumber !== undefined
            ) {
              existing.identificationType = traveller.identificationType;
              existing.identificationNumber = traveller.identificationNumber;
            }
          }
        });
      }
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuoteData.pending, (state) => {
        state.loading = true; // Update the state with the response data
        return state;
      })
      .addCase(getQuoteData.fulfilled, (state, action) => {
        const normalizedPayload = normalizePostageOption(action.payload);
        state = {
          ...state,
          loading: false,
          quoteCompleted: {
            completed: true,
          },
          ...normalizedPayload,
        } as QuoteData;
        return state;
      })
      .addCase(createQuoteOnQuestion.pending, (state) => {
        state.loading = false; // Update the state with the response data
        return state;
      })
      .addCase(createQuoteOnQuestion.fulfilled, (state, action) => {
        const normalizedPayload = normalizePostageOption(action.payload);
        state = {
          ...state,
          loading: false,
          ...normalizedPayload,
        } as QuoteData;
        return state;
      })
      .addCase(updateQuoteOnQuestion.pending, (state) => {
        state.loading = false; // Update the state with the response data
        return state;
      })
      .addCase(updateQuoteOnQuestion.fulfilled, (state, action) => {
        const normalizedPayload = normalizePostageOption(action.payload);
        state = {
          ...state,
          loading: false,
          ...normalizedPayload,
          quoteErrorCode: null,
        } as QuoteData;
        return state;
      })
      .addCase(updateQuoteOnQuestion.rejected, (state, action) => {
        const errorPayload = action.payload as ApiErrorProps;

        if (
          errorPayload.httpCode === 403 &&
          errorPayload.errors?.[0]?.code === 4107
        ) {
          return initialState;
        }
        state.error = action.payload as ApiErrorProps;
        return state;
      })
      .addCase(getQuoteData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ApiErrorProps;
        return state;
      })
      .addCase(createQuoteOnQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ApiErrorProps;
        state.turnstileToken = null;
        return state;
      })
      .addCase(saveQuestionDataBFF.fulfilled, (state, action) => {
        if (action.payload) {
          state.turnstileToken = null;
        }
        return state;
      });
  },
});

export const {
  clearQuote,
  setQuote,
  setQuoteErrorCode,
  clearQuoteErrorCode,
  updateQuote,
  updateQuoteCompleted,
  updateisLoadingCoverState,
  setCoverLevelChangedState,
  setTripTypeChangedState,
  setQuestionData,
  clearQuestion,
  updateOrganiserPMI,
  updateRequiredInformation,
  setTurnstileToken,
  setTurnstileErrorCode,
  setUnsupportedBrowserFlag,
  clearTurnstileErrorCode,
  clearTurnstileToken,
  setOnLoadingForExpiredToken,
} = quoteSlice.actions;
export default quoteSlice.reducer;
